/**
 * Store-First Data Pattern Utilities
 *
 * These utilities help implement the store-first pattern for data fetching hooks.
 * The pattern prioritizes Zustand store data (updated immediately by mutations)
 * over potentially stale request data from ahooks cache.
 *
 * Benefits:
 * - Instant UI updates after mutations (no waiting for API refresh)
 * - Optimistic UI experience
 * - Consistent data flow across the application
 *
 * @see CLAUDE.md "Store-First Pattern" section
 */

/**
 * Returns store data if available, otherwise falls back to request data.
 * Use this for array data (lists of items).
 *
 * @param storeData - Data from Zustand store (updated immediately by mutations)
 * @param requestData - Data from ahooks useRequest (may be stale/cached)
 * @param fallback - Default value if both are empty (default: [])
 * @returns The most up-to-date data, prioritizing store
 *
 * @example
 * ```tsx
 * const contactsFromStore = useContactsForVendor(vendorId);
 * const { data } = useRequest(() => contactApi.list(vendorId));
 *
 * return {
 *   contacts: getStoreFirstArray(contactsFromStore, data?.data),
 * };
 * ```
 */
export function getStoreFirstArray<T>(
  storeData: T[],
  requestData: T[] | undefined | null,
  fallback: T[] = []
): T[] {
  // If store has data, use it (mutations update store immediately)
  if (storeData && storeData.length > 0) {
    return storeData;
  }
  // Otherwise fall back to request data or default
  return requestData ?? fallback;
}

/**
 * Returns store data if available, otherwise falls back to request data.
 * Use this for single objects or primitives (pagination, detail records, etc.).
 *
 * @param storeData - Data from Zustand store (updated immediately by mutations)
 * @param requestData - Data from ahooks useRequest (may be stale/cached)
 * @param fallback - Default value if both are undefined (default: undefined)
 * @returns The most up-to-date data, prioritizing store
 *
 * @example
 * ```tsx
 * const paginationFromStore = usePaginationForVendor(vendorId);
 * const { data } = useRequest(() => contactApi.list(vendorId));
 *
 * return {
 *   pagination: getStoreFirstValue(paginationFromStore, data?.pagination),
 * };
 * ```
 */
export function getStoreFirstValue<T>(
  storeData: T | undefined | null,
  requestData: T | undefined | null,
  fallback?: T
): T | undefined {
  // If store has data, use it (mutations update store immediately)
  if (storeData !== undefined && storeData !== null) {
    return storeData;
  }
  // Otherwise fall back to request data or default
  return requestData ?? fallback;
}

/**
 * Generates a clean cache key for ahooks useRequest.
 * Handles undefined params gracefully to avoid "?undefined" in cache keys.
 *
 * @param basePath - The base API path (e.g., '/api/vendors/6/contacts')
 * @param params - Optional query parameters object
 * @returns A clean cache key string
 *
 * @example
 * ```tsx
 * // With params: '/api/vendors/6/contacts?{"page":1}'
 * // Without params: '/api/vendors/6/contacts'
 * cacheKey: getCacheKey(`/api/vendors/${vendorId}/contacts`, params)
 * ```
 */
export function getCacheKey<T extends object>(basePath: string, params?: T | null): string {
  if (params && Object.keys(params).length > 0) {
    return `${basePath}?${JSON.stringify(params)}`;
  }
  return basePath;
}
